// src/components/dashboard/DashboardLayout.jsx - FIXED VERSION
import React, { useState } from 'react';
import { 
  List, 
  FileText, 
  User, 
  LogOut, 
  Globe, 
  Settings, 
  Menu, 
  X, 
  ChevronDown,
  Bell,
  Home,
  SquareStack,
  Truck,
  Shield
} from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationDropdown from '../notifications/NotificationDropdown';
import RegistrationForm from './RegistrationForm';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const { unreadCount } = useNotifications();
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isRegistrationFormOpen, setIsRegistrationFormOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const languages = {
    en: 'English',
    hy: 'Հայերեն',
    ru: 'Русский'
  };

  const menuItems = [
    { 
      path: '/',
      icon: Home,
      label: 'home'
    },
    { 
      path: '/dashboard',
      icon: List,
      label: 'registrations'
    },
	{ 
      path: '/insurance',
      icon: Shield,
      label: 'insurance'
    },
    {
      path: '/invoices',
      icon: FileText,
      label: 'invoices'
    },
    {
      path: '/queue-russia',
      icon: SquareStack,
      label: 'queueToRussia'
    },
    {
      path: '/vehiclefleet',
      icon: Truck,
      label: 'vehicleFleet'
    },
    {
      path: '/consultation',
      icon: Settings,
      label: 'technicalConsultation'
    }
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login', { state: { from: 'logout' } });
  };

  const handleRegistrationSubmit = async (formData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/registrations/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Registration failed:", errorText);
        return;
      }
      
      setIsRegistrationFormOpen(false);
      window.location.reload();
    } catch (error) {
      console.error("Registration error:", error);
    }
  };

  // FIXED: Close all dropdowns when clicking outside
  const handleBackdropClick = () => {
    setShowNotifications(false);
    setShowLangDropdown(false);
  };

  return (
    <div className="flex h-screen bg-neutral-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-20 bg-neutral-900 bg-opacity-50 transition-opacity md:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-primary-800 text-white transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 transition duration-200 ease-in-out md:relative md:flex-shrink-0 flex flex-col`}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-primary-700">
          <Link to="/" className="flex-shrink-0">
            <span className="text-xl font-bold tracking-wide bg-white text-primary-700 px-3 py-1 rounded-md">TTS</span>
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="md:hidden p-2 rounded-md hover:bg-primary-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Sidebar content */}
        <div className="flex-1 overflow-y-auto">
          <nav className="mt-5 px-2 space-y-1">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-3 py-3 text-sm font-medium rounded-md hover:bg-primary-700 ${
                  (location.pathname === item.path) || 
                  (item.path !== '/' && location.pathname.startsWith(item.path))
                    ? 'bg-primary-900 text-white' 
                    : 'text-primary-100'
                }`}
              >
                <item.icon className={`mr-3 h-5 w-5 ${
                  (location.pathname === item.path) || 
                  (item.path !== '/' && location.pathname.startsWith(item.path))
                    ? 'text-white' 
                    : 'text-primary-300 group-hover:text-white'
                }`} />
                {t(item.label)}
              </Link>
            ))}
          </nav>
        </div>

        {/* User profile section - Fixed at bottom */}
        <div className="border-t border-primary-700 mt-auto">
          <div className="px-3 py-3 flex items-center bg-primary-700">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
                <User className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-white">{user?.username || 'Guest'}</p>
            </div>
            <button 
              onClick={handleLogout}
              className="p-2 rounded-md hover:bg-primary-600 text-white"
              title={t('logout')}
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top navigation */}
        <header className="bg-white shadow-sm z-10 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              {/* Mobile menu button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100"
              >
                <Menu className="h-6 w-6" />
              </button>
              
              {/* RIGHT SIDE - Notifications, Bell and Language */}
              <div className="flex items-center space-x-4 ml-auto">
                {/* Notifications */}
                <div className="relative">
                  <button 
                    data-notification-trigger // FIXED: Add identifier for the notification trigger
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowNotifications(!showNotifications);
                      setShowLangDropdown(false); // Close language dropdown
                    }}
                    className="p-2 text-neutral-500 hover:text-neutral-900 relative rounded-full hover:bg-neutral-100 transition-colors"
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown - FIXED positioning */}
                  {showNotifications && (
                    <div className="absolute right-0 mt-2" style={{ zIndex: 10001 }}>
                      <NotificationDropdown onClose={() => setShowNotifications(false)} />
                    </div>
                  )}
                </div>

                {/* Language Selector */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLangDropdown(!showLangDropdown);
                      setShowNotifications(false); // Close notifications dropdown
                    }}
                    className="flex items-center space-x-2 px-3 py-2 rounded-md text-neutral-600 hover:bg-neutral-100 transition duration-150"
                  >
                    <Globe className="h-5 w-5" />
                    <span className="text-sm font-medium">{languages[language]}</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>

                  {showLangDropdown && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowLangDropdown(false)}
                      />
                      <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                        <div className="py-1">
                          {Object.entries(languages).map(([code, name]) => (
                            <button
                              key={code}
                              onClick={() => {
                                setLanguage(code);
                                setShowLangDropdown(false);
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                            >
                              {name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-neutral-50 p-4 sm:p-6 lg:p-8">
          {/* Registration Form Modal */}
          {isRegistrationFormOpen && (
            <RegistrationForm
              isOpen={isRegistrationFormOpen}
              onClose={() => setIsRegistrationFormOpen(false)}
              onSubmit={handleRegistrationSubmit}
            />
          )}
          
          {/* Main content container */}
          <div className="w-full">
            {children}
          </div>
        </main>
      </div>


    </div>
  );
};

export default DashboardLayout;